import { useMemo } from 'react';

import { isMinor, mapServerError } from 'core';
import {
  useLazyCheckEmailQuery,
  useRegisterAnonymousUserMutation,
  useRegisterLoggedUserMutation,
  useRegisterUserMutation,
  useSendAccountEmailMutation,
  useSendParentAccountInvitationMutation,
} from 'store/signup/signupAPI';

import { some } from 'lodash-es';

import { ServerError } from '@britishcouncil/react-forms';
import { OidcContext } from '@britishcouncil/react-oidc-auth';

import { SignupFormModel } from './types';

const LOGOUT_TIMEOUT = 1000 * 4; // wait 4 seconds before logout

export const useSignupSubmit = () => {
  const userManager = OidcContext.useAuth();
  const [checkEmail, { data: checkEmailStatus, isFetching: emailChecking }, { lastArg: lastCheckedEmail }] =
    useLazyCheckEmailQuery();
  const [registerUser, registerUserData] = useRegisterUserMutation();
  const [sendAccountEmail, sendAccountEmailData] = useSendAccountEmailMutation();
  const [sendParentInvitation, sendParentInvitationData] = useSendParentAccountInvitationMutation();
  const [registerAnonymousUser, registerAnonymousUserData] = useRegisterAnonymousUserMutation();
  const [registerLoggedUser, registerLoggedUserData] = useRegisterLoggedUserMutation();

  const userStatus = useMemo<'new' | 'anonymous' | 'exist'>(() => {
    if (checkEmailStatus?.existInEam && !checkEmailStatus?.existInSRS) return 'anonymous';
    if (checkEmailStatus?.existInEam && checkEmailStatus?.existInSRS) return 'exist';
    return 'new';
  }, [checkEmailStatus]);

  const logoutIfRequired = (loggedUser: boolean) => {
    if (loggedUser) {
      setTimeout(() => {
        userManager.signoutRedirect();
      }, LOGOUT_TIMEOUT);
    }
  };

  const submit = (values: SignupFormModel, loggedUser: boolean) => {
    if (isMinor(values.dateOfBirth)) {
      sendParentInvitation(values)
        .unwrap()
        .then(() => logoutIfRequired(loggedUser));
    } else if (loggedUser) {
      registerLoggedUser(values)
        .unwrap()
        .then(() => logoutIfRequired(loggedUser));
    } else if (userStatus === 'exist') {
      sendAccountEmail(values.email);
    } else if (userStatus === 'anonymous') {
      registerAnonymousUser(values);
    } else {
      //TODO[GP - future fix] - remove confirmPassword on API side
      registerUser({ ...values, confirmPassword: values.password });
    }
  };

  const serverErrors = useMemo<ServerError[]>(() => {
    let lastMutation = [
      registerUserData,
      sendAccountEmailData,
      sendParentInvitationData,
      registerAnonymousUserData,
      registerLoggedUserData,
    ].sort((x, y) => (y.startedTimeStamp ?? 0) - (x.startedTimeStamp ?? 0))[0];

    if (!lastMutation.isError) return [];

    return mapServerError(lastMutation.error);
  }, [
    registerUserData,
    sendAccountEmailData,
    sendParentInvitationData,
    registerAnonymousUserData,
    registerLoggedUserData,
  ]);

  return {
    checkEmail,
    emailChecking,
    lastCheckedEmail,
    userStatus,
    submit,
    registrationCompleted: some([
      registerUserData.isSuccess,
      sendAccountEmailData.isSuccess,
      sendParentInvitationData.isSuccess,
      registerAnonymousUserData.isSuccess,
      registerLoggedUserData.isSuccess,
    ]),
    parentInvitationSend: sendParentInvitationData.isSuccess,
    isSubmitting:
      registerUserData.isLoading ||
      sendAccountEmailData.isLoading ||
      sendParentInvitationData.isLoading ||
      registerAnonymousUserData.isLoading ||
      registerLoggedUserData.isLoading,
    serverErrors,
  };
};
